<template>
  <div class="factory-dashboard" ref="dashboardWrap">
    <!-- 总览页面 -->
    <div v-if="page === 'dashboard'" class="page page-dashboard">
      <header class="header-bar">
        <div class="header-left">
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="32" height="32" rx="8" stroke="url(#lg1)" stroke-width="2" fill="rgba(77,154,255,0.08)"/>
            <path d="M10 26V14l8-6 8 6v12H22v-7h-8v7h-4z" fill="url(#lg2)" opacity="0.9"/>
            <circle cx="18" cy="16" r="2.5" fill="#00e0f0" opacity="0.8"/>
            <defs>
              <linearGradient id="lg1" x1="2" y1="2" x2="34" y2="34">
                <stop stop-color="#4d9aff"/>
                <stop offset="1" stop-color="#00e0f0"/>
              </linearGradient>
              <linearGradient id="lg2" x1="10" y1="8" x2="26" y2="26">
                <stop stop-color="#4d9aff"/>
                <stop offset="1" stop-color="#00e0f0"/>
              </linearGradient>
            </defs>
          </svg>
          <div class="header-logo-text">
            <span class="logo-en">FACTORY PANORAMA</span>
            <span class="logo-cn">智能生产监控平台</span>
          </div>
        </div>
        <div class="header-center">
          <h1 class="company-title">上海和峰印务科技有限公司</h1>
          <span class="company-en">SHANGHAI HEFENG PRINTING TECHNOLOGY</span>
        </div>
        <div class="header-right">
          <div class="header-date">
            <i class="fas fa-calendar-alt"></i>
            <span>{{ headerDate }}</span>
          </div>
          <div class="header-time">{{ headerTime }}</div>
          <div class="online-tag">
            <span class="sdot running"></span>
            <span>系统在线</span>
          </div>
        </div>
        <div class="scan-line"></div>
      </header>

      <main class="main-container">
        <!-- 左侧边栏 -->
        <aside class="aside-left">
          <!-- 每日印量分析 -->
          <div class="card fade-in">
            <div class="card-title">每日印量分析</div>
            <div class="total-print-box">
              <div class="sub-tip">今日总印量（张）</div>
              <div class="big-num">{{ formatNum(totalPrintCount) }}</div>
            </div>
            <div class="two-col-grid">
              <div class="stat-item cyan">
                <div class="sub-tip">时间稼动率</div>
                <div class="stat-val cyan">{{ avgTimeUtil }}%</div>
              </div>
              <div class="stat-item blue">
                <div class="sub-tip">性能稼动率</div>
                <div class="stat-val blue">{{ avgPerfUtil }}%</div>
              </div>
            </div>
          </div>

          <!-- 工作时长统计 -->
          <div class="card fade-in">
            <div class="card-title">工作时长统计</div>
            <div class="ring-center-wrap">
              <RingChart
                  chartId="workDurationRing"
                  :width="`120px`"
                  :height="'120px'"
                  :total="String(workDurationData.total)"
                  :text="'总时长(h)'"
                  total-top="50%"
                  total-left="50%"
                  :radius="['60%', '85%']"
                  :center="['50%', '50%']"
                  :legend-show="false"
                  :data="workRingData"
                  :color="['#00e0f0', '#ffb347', '#ff6b7a']"
              />
            </div>
            <div class="duration-list">
              <div class="dur-row">
                <span class="dot cyan"></span>
                <span class="dur-label">工作时长</span>
                <div class="dur-bar">
                  <div class="dur-fill cyan" :style="{width: workPct + '%'}"></div>
                </div>
                <span class="dur-h cyan">{{ workDurationData.work }}h</span>
                <span class="dur-pct">{{ workPct }}%</span>
              </div>
              <div class="dur-row">
                <span class="dot warn"></span>
                <span class="dur-label">待机时长</span>
                <div class="dur-bar">
                  <div class="dur-fill warn" :style="{width: standbyPct + '%'}"></div>
                </div>
                <span class="dur-h warn">{{ workDurationData.standby }}h</span>
                <span class="dur-pct">{{ standbyPct }}%</span>
              </div>
              <div class="dur-row">
                <span class="dot danger"></span>
                <span class="dur-label">停机时长</span>
                <div class="dur-bar">
                  <div class="dur-fill danger" :style="{width: downPct + '%'}"></div>
                </div>
                <span class="dur-h danger">{{ workDurationData.downtime }}h</span>
                <span class="dur-pct">{{ downPct }}%</span>
              </div>
            </div>
          </div>

          <!-- 车间环境 -->
          <div class="card fade-in flex-col-grow">
            <div class="card-title">车间环境</div>
            <div class="env-grid">
              <div class="env-ring-item">
                <div class="svg-ring">
                  <!-- 温度环形进度 -->
                  <el-progress
                      type="circle"
                      :percentage="(Number(envTemp).toFixed(1))"
                      :stroke-width="7"
                      :width="100"
                      color="#ff6b7a"
                      define-back-color="rgba(255,107,122,0.1)"
                  >
                  </el-progress>
                  <div class="progress-inner">
                    <span class="ring-num danger">{{ envTemp }}</span>
                    <span class="ring-tip">°C</span>
                  </div>
                </div>
                <div class="env-label">
                  <i class="fas fa-temperature-high"></i>
                  <span>车间温度</span>
                </div>
              </div>

              <div class="env-ring-item">
                <div class="svg-ring">
                  <!-- 湿度环形进度 -->
                  <el-progress
                      type="circle"
                      :percentage="Number(envHumid)"
                      :stroke-width="7"
                      :width="100"
                      color="#4d9aff"
                      define-back-color="rgba(77,154,255,0.1)"
                  >
                  </el-progress>
                  <div class="progress-inner">
                    <span class="ring-num blue">{{ envHumid }}</span>
                    <span class="ring-tip">%</span>
                  </div>
                </div>
                <div class="env-label">
                  <i class="fas fa-tint"></i>
                  <span>车间湿度</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <!-- 中间设备监控 -->
        <section class="section-middle">
          <div class="card flex-col-grow">
            <div class="card-title">设备监控</div>
            <div class="equip-grid" :style="{gridTemplateColumns: equipGridCol}">
              <div v-for="(item, idx) in allEquipList" :key="item.id" class="equip-card fade-in"
                   :style="{animationDelay: 0.02 + idx * 0.03 + 's'}" @click="openDetail(item)">
                <div class="equip-card-head">
                  <div class="equip-name-row">
                    <span class="sdot" :class="statusDotClass(item.status)"></span>
                    <span class="equip-name">{{ item.name }}</span>
                  </div>
                  <span class="status-tag" :style="statusTagStyle(item.status)">
                    {{ statusText(item.status) }}
                  </span>
                </div>
                <div class="equip-thumb">
                  <img :src="item.url" alt="">
                </div>
                <div class="equip-pie-row">
                  <div class="pie-wrap">
                    <RingChart
                        :chartId="'equipRing' + item.id"
                        :width="`120px`"
                        :height="'120px'"
                        :text="'总印量'"
                        :total="formatNum(item.count)"
                        total-top="50%"
                        total-left="50%"
                        :radius="['60%', '85%']"
                        :center="['50%', '50%']"
                        :legend-show="false"
                        :data="equipRingData(item.id)"
                        :color="['#00e0f0','#ffb347']"
                    />
                  </div>
                  <div class="pie-data-list">
                    <div class="data-row cyan-bg">
                      <span class="data-row-item">
                        <span class="mini-dot cyan"></span>
                        <span class="title">有效印量</span>
                      </span>
                      <span class="data-num cyan">{{ formatNum(item.okCount) }}</span>
                    </div>
                    <div class="data-row warn-bg">
                      <span class="data-row-item">
                         <span class="mini-dot warn"></span>
                        <span class="title">过版印量</span>
                      </span>
                      <span class="data-num warn">{{ formatNum(item.noCount) }}</span>
                    </div>
                  </div>
                </div>
                <div class="order-stat-row">
                  <div class="stat-mini">
                    <div class="stat-mini-tip">已完成订单</div>
                    <div class="stat-mini-val cyan">{{ item.completedOrders }}单</div>
                  </div>
                  <div class="stat-mini">
                    <div class="stat-mini-tip">待生产订单</div>
                    <div class="stat-mini-val warn">{{ item.pendingOrders }}单</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 右侧边栏 -->
        <aside class="aside-right">
          <!-- 订单概况 -->
          <div class="card fade-in">
            <div class="card-title">订单概况</div>
            <div class="order-overview-row">
              <div class="overview-pie-wrap">
                <RingChart
                    chartId="orderOverviewRing"
                    :width="`120px`"
                    :height="'120px'"
                    :text="'总订单'"
                    :total="String(totalOrderCount)"
                    total-top="50%"
                    total-left="50%"
                    :radius="['55%', '85%']"
                    :center="['50%', '50%']"
                    :legend-show="false"
                    :data="[
                    {name:'已完成',value:ovCompleted},
                    {name:'生产中',value:ovInProgress},
                    {name:'待生产',value:ovPending}
                  ]"
                    :color="['#00e0f0','#4d9aff','#ffb347']"
                />
              </div>
              <div class="overview-stat-list">
                <div class="ov-stat cyan-bg">
                  <div class="ov-left">
                    <span class="mini-dot cyan"></span>
                    <span>已完成</span>
                  </div>
                  <div class="ov-num cyan">{{ ovCompleted }}</div>
                </div>
                <div class="ov-stat blue-bg">
                  <div class="ov-left">
                    <span class="mini-dot blue"></span>
                    <span>生产中</span>
                  </div>
                  <div class="ov-num blue">{{ ovInProgress }}</div>
                </div>
                <div class="ov-stat warn-bg">
                  <div class="ov-left">
                    <span class="mini-dot warn"></span>
                    <span>待生产</span>
                  </div>
                  <div class="ov-num warn">{{ ovPending }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 订单滚动列表 -->
          <div class="card fade-in flex-col-grow order-scroll-card">
            <div class="card-title">
              订单信息
              <span class="scroll-tip">实时滚动</span>
            </div>
            <div class="scroll-container">
              <div class="scroll-inner" :style="scrollStyle">
                <div v-for="(item, idx) in scrollOrderList" :key="idx" class="order-scroll-item">
                  <div class="order-info">
                    <div class="order-name">{{ item.name }}</div>
                    <div class="order-sub">{{ item.id }} · {{ item.equipName }}</div>
                  </div>
                  <div class="order-qty-box">
                    <div class="qty-num">{{ formatNum(item.qty) }}</div>
                    <div class="qty-tip">张</div>
                  </div>
                  <span class="tag" :class="orderTagClass(item.status)">{{ orderStatusText(item.status) }}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </main>
    </div>

  </div>
</template>
<script>
import API from "@/api";
// import * as echarts from 'echarts'
import RingChart from '@/components/Charts/RingChartdevice.vue'
import LineChart from '@/components/Charts/LineChartDevice.vue'

export default {
  name: 'PrintMonitorDashboard',
  components: {
    RingChart,
    LineChart
  },
  data() {
    return {
      page: 'dashboard',
      headerDate: '',
      headerTime: '',
      totalPrintCount: 0,
      ovCompleted: 0,
      ovInProgress: 0,
      ovPending: 0,
      totalOrderCount: 0,
      avgTimeUtil: 0,
      avgPerfUtil: 0,
      allEquipList: [],
      equipGridCol: 'repeat(3, 1fr)',
      scrollOrderList: [],
      scrollStyle: { transform: 'translateY(0px)' },
      scrollTimer: null,
      envTemp: 0,
      envHumid: 0,
      currentEquip: null,
      detailTotalOrder: 0,
      detailCompletedPct: 0,
      detailPendingPct: 0,
      detailValidPct: 0,
      detailOverPct: 0,
      workDurationData: {
        total: 0,
        work: 0,
        standby: 0,
        downtime: 0
      },
      speedLineData: {},
      qualityLineData: {}
    };
  },
  computed: {

    equipRingData() {
      return (id) => [
        { name: '有效印', value: this.allEquipList.find(e => e.id === id)?.okCount || 0 },
        { name: '过版', value: this.allEquipList.find(e => e.id === id)?.noCount || 0 }
      ];
    },
    workPct() {
      return this.workDurationData.total > 0 ? ((this.workDurationData.work / this.workDurationData.total) * 100).toFixed(1) : 0;
    },
    standbyPct() {
      return this.workDurationData.total > 0 ? ((this.workDurationData.standby / this.workDurationData.total) * 100).toFixed(1) : 0;
    },
    downPct() {
      return this.workDurationData.total > 0 ? ((this.workDurationData.downtime / this.workDurationData.total) * 100).toFixed(1) : 0;
    },
    workRingData() {
      return [
        { name: '工作时长', value: this.workDurationData.work },
        { name: '待机时长', value: this.workDurationData.standby },
        { name: '停机时长', value: this.workDurationData.downtime }
      ];
    },
  },
  mounted() {
    this.getDaPingJiTaiXinLing()
    this.initData();
    this.updateClock();
    setInterval(() => this.updateClock(), 1000);

    this.$nextTick(() => {
      this.startScroll();
    });
  },
  beforeDestroy() {
    clearInterval(this.refreshTimer);
    if (this.scrollTimer) clearInterval(this.scrollTimer);
  },
  methods: {
    getDaPingJiTaiXinLing() {
      API.DaPingCheJianXinLing({teamId:'9bfdb48d-0cf5-4599-b41c-e351f98c5335'}).then(res => {
        console.log(res)
        this.totalPrintCount = res.message.data.count;
        this.avgTimeUtil = res.message.data.timeOee
        this.avgPerfUtil = res.message.data.chanNengOee
        this.workDurationData.total = Number((Number(res.message.data.time) + Number(res.message.data.daiJiTime) + Number(res.message.data.tingJiTime)).toFixed(1))
        this.workDurationData.work = Number(res.message.data.time).toFixed(1)
        this.workDurationData.standby = Number(res.message.data.daiJiTime).toFixed(1)
        this.workDurationData.downtime = Number(res.message.data.tingJiTime).toFixed(1)
        this.envTemp = res.message.data.temperature
        this.envHumid = res.message.data.humidity
        // this.allEquipList = res.message.data.deviceInfos
        // 1. 先赋值设备列表
        let deviceList = res.message.data.deviceInfos || []

        // 2. 遍历每台设备，新增两个自定义字段，随机赋值（区间可按需修改）
        deviceList.forEach(item => {
          // 已完成订单：3 ~ 18 随机整数
          item.completedOrders = Math.floor(Math.random() * 16) + 3
          // 待生产订单：2 ~ 6 随机整数
          item.pendingOrders = Math.floor(Math.random() * 5) + 2
        })

        // 3. 赋值给页面列表
        this.allEquipList = deviceList
        this.scrollOrderList = [
            { id: 'ORD-0115-001', name: '画册-春日花园', status: 'completed', qty: 5000 },
                { id: 'ORD-0115-002', name: '包装盒-红茶礼盒', status: 'inProgress', qty: 12000 },
                { id: 'ORD-0115-003', name: '宣传单-新品发布', status: 'inProgress', qty: 80000 },
                { id: 'ORD-0115-004', name: '手提袋-品牌定制', status: 'pending', qty: 3000 },
                { id: 'ORD-0115-005', name: '杂志-月刊第48期', status: 'pending', qty: 15000 },
                { id: 'ORD-0115-006', name: '名片-商务系列', status: 'pending', qty: 50000 },
                { id: 'ORD-0115-007', name: '海报-艺术展', status: 'completed', qty: 20000 },
                { id: 'ORD-0115-008', name: '折页-地产宣传', status: 'inProgress', qty: 45000 },
                { id: 'ORD-0115-009', name: '信封-企业定制', status: 'pending', qty: 8000 },
                { id: 'ORD-0115-010', name: '不干胶-标签系列', status: 'pending', qty: 100000 },
                { id: 'ORD-0115-011', name: '书籍-技术手册', status: 'completed', qty: 6000 },
                { id: 'ORD-0115-012', name: '台历-新年定制', status: 'completed', qty: 3000 },
                { id: 'ORD-0115-013', name: '瓦楞纸箱-电商', status: 'inProgress', qty: 25000 },
                { id: 'ORD-0115-014', name: '彩盒-月饼礼盒', status: 'inProgress', qty: 18000 },
                { id: 'ORD-0115-015', name: '标签-食品系列', status: 'inProgress', qty: 50000 },
                { id: 'ORD-0115-016', name: '说明书-电子配件', status: 'pending', qty: 40000 },
                { id: 'ORD-0115-017', name: '画册-旅游景点', status: 'pending', qty: 8000 },
                { id: 'ORD-0115-018', name: '手提袋-购物袋', status: 'pending', qty: 5000 },
                { id: 'ORD-0115-019', name: '纸杯-咖啡店', status: 'pending', qty: 20000 },
                { id: 'ORD-0115-020', name: '名片-烫金系列', status: 'completed', qty: 20000 },
                { id: 'ORD-0115-021', name: '优惠券-餐饮连锁', status: 'completed', qty: 80000 },
                { id: 'ORD-0115-022', name: '贺卡-新年祝福', status: 'pending', qty: 10000 },
                { id: 'ORD-0115-023', name: '档案袋-政府定制', status: 'pending', qty: 5000 },
                { id: 'ORD-0115-024', name: '信纸-企业文宣', status: 'pending', qty: 15000 },
                { id: 'ORD-0115-025', name: '包装-化妆品盒', status: 'completed', qty: 15000 },
                { id: 'ORD-0115-026', name: '宣传册-汽车4S', status: 'completed', qty: 30000 },
                { id: 'ORD-0115-027', name: '便签本-文具系列', status: 'inProgress', qty: 20000 },
                { id: 'ORD-0115-028', name: '地图-旅游指南', status: 'inProgress', qty: 8000 },
                { id: 'ORD-0115-029', name: '纸袋-服装品牌', status: 'pending', qty: 6000 },
                { id: 'ORD-0115-030', name: '卡片-会员卡', status: 'pending', qty: 50000 },
                { id: 'ORD-0115-031', name: '表格-财务报表', status: 'completed', qty: 4000 },
                { id: 'ORD-0115-032', name: '证书-荣誉证书', status: 'completed', qty: 1000 },
                { id: 'ORD-0115-033', name: '请柬-婚礼定制', status: 'completed', qty: 2000 },
                { id: 'ORD-0115-034', name: '入场券-演出活动', status: 'pending', qty: 15000 },
                { id: 'ORD-0115-035', name: '制版-画册封面', status: 'completed', qty: 2000 },
                { id: 'ORD-0115-036', name: '制版-包装彩盒', status: 'inProgress', qty: 3500 },
                { id: 'ORD-0115-037', name: '制版-宣传海报', status: 'inProgress', qty: 1800 },
                { id: 'ORD-0115-038', name: '制版-手提袋', status: 'pending', qty: 2400 },
                { id: 'ORD-0115-039', name: '制版-杂志内页', status: 'completed', qty: 1500 },
                { id: 'ORD-0115-040', name: '制版-书籍封面', status: 'inProgress', qty: 2800 },
                { id: 'ORD-0115-041', name: '制版-折页单页', status: 'pending', qty: 3000 },
                { id: 'ORD-0115-042', name: '制版-名片拼版', status: 'pending', qty: 1200 },
                { id: 'ORD-0115-043', name: '胶装-技术手册', status: 'completed', qty: 3000 },
                { id: 'ORD-0115-044', name: '胶装-杂志合订', status: 'inProgress', qty: 5000 },
                { id: 'ORD-0115-045', name: '胶装-教材课本', status: 'inProgress', qty: 8000 },
                { id: 'ORD-0115-046', name: '胶装-年度报告', status: 'pending', qty: 2000 },
                { id: 'ORD-0115-047', name: '模切-药盒', status: 'completed', qty: 10000 },
                { id: 'ORD-0115-048', name: '模切-化妆品盒', status: 'completed', qty: 8000 },
                { id: 'ORD-0115-049', name: '模切-食品包装', status: 'pending', qty: 12000 },
                { id: 'ORD-0115-050', name: '模切-电子配件盒', status: 'pending', qty: 6000 },
                { id: 'ORD-0115-051', name: '模切-礼品盒', status: 'pending', qty: 4000 },
                { id: 'ORD-0115-052', name: '覆膜-精装封面', status: 'completed', qty: 6000 },
                { id: 'ORD-0115-053', name: '覆膜-手提袋', status: 'completed', qty: 10000 },
                { id: 'ORD-0115-054', name: '覆膜-包装彩盒', status: 'inProgress', qty: 15000 },
                { id: 'ORD-0115-055', name: '覆膜-挂历封面', status: 'inProgress', qty: 8000 },
                { id: 'ORD-0115-056', name: '覆膜-礼盒外盒', status: 'pending', qty: 5000 },
                { id: 'ORD-0115-057', name: '覆膜-书封', status: 'pending', qty: 12000 },
                { id: 'ORD-0115-058', name: '烫金-礼盒logo', status: 'completed', qty: 3000 },
                { id: 'ORD-0115-059', name: '烫金-贺卡封面', status: 'completed', qty: 5000 },
                { id: 'ORD-0115-060', name: '烫金-酒标', status: 'completed', qty: 8000 },
                { id: 'ORD-0115-061', name: '烫金-邀请函', status: 'pending', qty: 2000 },
                { id: 'ORD-0115-062', name: '烫金-商标贴纸', status: 'pending', qty: 6000 }
            ]
        this.ovCompleted = 110
        this.ovInProgress = 15
        this.ovPending = 24
        this.totalOrderCount = 149
        clearInterval(this.scrollTimer)
        this.startScroll()
      })
    },
    initData() {
      this.equipGridCol = this.allEquipList.length <= 4 ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)';
    },


    updateClock() {
      const now = new Date();
      this.headerDate = now.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short'
      });
      this.headerTime = now.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    startScroll() {
      const totalHeight = this.scrollOrderList.length * 62;

      this.scrollTimer = setInterval(() => {
        let y = parseFloat(this.scrollStyle.transform.replace(/[^0-9.-]/g, '') || 0);
        y -= 1;
        if (Math.abs(y) >= totalHeight) {
          y = 0;
        }
        this.scrollStyle.transform = `translateY(${y}px)`;
      }, 50);
    },


    openDetail(item) {
      this.$router.push({
        path: '/PrintMonitorDeviceDetail',
        query: { deviceId: item.id, completedOrders:item.completedOrders,pendingOrders:item.pendingOrders,url:item.url}
      })
      // API.DaPingJiTaiXinLing({deviceId: item.id}).then(res => {
      //   console.log(res)
      //   this.currentEquip = res.message.data;
      //   this.currentEquip.currentJob = '包装盒-红茶礼盒'
      //   this.currentEquip.jobStart = '08:30'
      //   this.currentEquip.jobEnd = '14:50'
      //   this.currentEquip.pendingOutput = '2670'
      //   console.log([...res.message.data.speedList])
      //   const speedSource = res.message.data.speedList.map(item => {
      //     // 只取日期后半段 07-01 这种，更简洁好看
      //     const day = item.time.split('-').slice(1).join('-');
      //     return [day, item.speed];
      //   });
      //   const printSource = res.message.data.printList.map(item => {
      //     // 只取日期后半段 07-01 这种，更简洁好看
      //     const day = item.time.split('-').slice(1).join('-');
      //     return [day, item.score];
      //   });
      //   this.speedLineData = {
      //     source: [
      //       ['时间', '运行速度'],
      //         ...speedSource
      //     ],
      //     series: [
      //       { name: '运行速度', type: 'line', color: '#4d9aff' }
      //     ],
      //     yAxis: {
      //       type: 'value',
      //       // name: '张/时',
      //       axisLabel: { formatter: '{value}k' },
      //       splitLine: {
      //         show: true,
      //         lineStyle: {
      //           color: 'rgba(255, 255, 255, 0.1)', // 横网格颜色
      //           width: 1,
      //           type: 'solid'
      //         }
      //       },
      //     },
      //     tooltip: {
      //       trigger: 'axis',
      //       formatter: (params) => {
      //         // params[0] 就是第一条线的数据
      //         const name = params[0].name;
      //         const value = params[0].data[1];
      //         return `${name}<br/>速度: ${value} 张/时`;
      //       }
      //     }
      //   }
      //   this.qualityLineData = {
      //     source: [
      //       ['时间', '质量得分'],
      //         ...printSource
      //     ],
      //     series: [
      //       { name: '质量得分', type: 'line', color: '#3ee8a5' }
      //     ],
      //     yAxis: {
      //       type: 'value',
      //       // name: '分数',
      //       min: 60,
      //       max: 100,
      //       axisLabel: { formatter: '{value}分' },
      //       splitLine: {
      //         show: true,
      //         lineStyle: {
      //           color: 'rgba(255, 255, 255, 0.1)', // 横网格颜色
      //           width: 1,
      //           type: 'solid'
      //         }
      //       },
      //     },
      //     tooltip: {
      //       trigger: 'axis',
      //       formatter: (params) => {
      //         const date = params[0].name;
      //         const speed = params[0].data[1];
      //         return `${date}<br/>速度: ${speed} 张/时`;
      //       }
      //     }
      //   }
      //   this.currentEquip.completedOrders = item.completedOrders
      //   this.currentEquip.pendingOrders = item.pendingOrders
      //   this.detailTotalOrder = this.currentEquip.completedOrders + this.currentEquip.pendingOrders
      //   this.detailCompletedPct =
      //       this.detailTotalOrder > 0
      //           ? ((this.currentEquip.completedOrders / this.detailTotalOrder) * 100).toFixed(1)
      //           : 0;
      //   this.detailPendingPct =
      //       this.detailTotalOrder > 0
      //           ? ((this.currentEquip.pendingOrders / this.detailTotalOrder) * 100).toFixed(1)
      //           : 0;
      //   this.currentEquip.orders = [
      //     { id: 'ORD-0115-002', name: '包装盒-红茶礼盒', status: 'inProgress', qty: 12000 },
      //     { id: 'ORD-0115-003', name: '宣传单-新品发布', status: 'inProgress', qty: 80000 },
      //     { id: 'ORD-0115-004', name: '手提袋-品牌定制', status: 'pending', qty: 3000 },
      //     { id: 'ORD-0115-005', name: '杂志-月刊第48期', status: 'pending', qty: 15000 },
      //     { id: 'ORD-0115-006', name: '名片-商务系列', status: 'pending', qty: 50000 },
      //     { id: 'ORD-0115-007', name: '海报-艺术展', status: 'completed', qty: 20000 },
      //     { id: 'ORD-0115-008', name: '折页-地产宣传', status: 'inProgress', qty: 45000 },
      //     { id: 'ORD-0115-009', name: '信封-企业定制', status: 'pending', qty: 8000 },
      //     { id: 'ORD-0115-010', name: '不干胶-标签系列', status: 'pending', qty: 100000 },
      //     { id: 'ORD-0115-011', name: '书籍-技术手册', status: 'completed', qty: 6000 },
      //     { id: 'ORD-0115-012', name: '台历-新年定制', status: 'completed', qty: 3000 },
      //     { id: 'ORD-0115-013', name: '瓦楞纸箱-电商', status: 'inProgress', qty: 25000 },
      //     { id: 'ORD-0115-014', name: '彩盒-月饼礼盒', status: 'inProgress', qty: 18000 },
      //     { id: 'ORD-0115-015', name: '标签-食品系列', status: 'inProgress', qty: 50000 },
      //     { id: 'ORD-0115-016', name: '说明书-电子配件', status: 'pending', qty: 40000 },
      //   ]
      //   this.detailValidPct =
      //       this.currentEquip.count > 0
      //           ? ((this.currentEquip.okCount / this.currentEquip.count) * 100).toFixed(1)
      //           : 0;
      //   this.detailOverPct =
      //       this.currentEquip.count > 0
      //           ? ((this.currentEquip.noCount / this.currentEquip.count) * 100).toFixed(1)
      //           : 0;
      //   // this.unfinishMaintenance =
      //   //     this.currentEquip.maintenance.filter(m => m.status === 'incomplete');
      // })
      // this.currentEquip = this.allEquipList.find(e => e.id === item.id);
      // if (!this.currentEquip) return;
      //
      // this.page = 'detail';

    },

    goBack() {
      this.currentEquip = null;
      this.page = 'dashboard';
    },
    formatNum(n) {
      return String(n ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    statusText(s) {
      return s === 1 ? '运行中' : s === 0 ? '待产' : '停机';
    },

    statusDotClass(s) {
      return s === 1 ? 'running' : s === 0 ? 'waiting' : 'stopped';
    },

    statusTagStyle(s) {
      return {
        background:
            s === 1
                ? 'rgba(0,224,240,0.1)'
                : s === 0
                ? 'rgba(255,179,71,0.1)'
                : 'rgba(255,107,122,0.1)',
        color:
            s === 1
                ? '#00e0f0'
                : s === 0
                ? '#ffb347'
                : '#ff6b7a'
      };
    },

    orderStatusText(s) {
      return s === 'completed' ? '已完成' : s === 'inProgress' ? '生产中' : '待生产';
    },

    orderTagClass(s) {
      return s === 'completed'
          ? 'tag-completed'
          : s === 'inProgress'
              ? 'tag-inProgress'
              : 'tag-pending';
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&family=Noto+Sans+SC:wght@300;400;500;700;900&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css');

:root {
  --bg: #080c16;
  --card: rgba(16, 22, 38, 0.94);
  --border: rgba(59, 139, 255, 0.14);
  --accent: #4d9aff;
  --accent2: #00e0f0;
  --warn: #ffb347;
  --danger: #ff6b7a;
  --fg: #f0f5fc;
  --muted: #94a7c8;
  --ring-bg: rgba(255, 255, 255, 0.05);
  --stat-bg: rgba(59, 139, 255, 0.07);
  --quality: #3ee8a5;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-size: 16px;
}

body {
  font-family: 'Noto Sans SC', sans-serif;
  background: var(--bg);
  color: var(--fg);
  overflow: hidden;
  height: 100vh;
  width: 100vw;
}

body::before {
  content: '';
  position: fixed;
  inset: 0;
  background:
      repeating-linear-gradient(
          0deg,
          transparent,
          transparent 59px,
          rgba(59, 139, 255, 0.018) 59px,
          rgba(59, 139, 255, 0.018) 60px
      ),
      repeating-linear-gradient(
          90deg,
          transparent,
          transparent 59px,
          rgba(59, 139, 255, 0.018) 59px,
          rgba(59, 139, 255, 0.018) 60px
      );
  pointer-events: none;
  z-index: 0;
}

body::after {
  content: '';
  position: fixed;
  inset: 0;
  background:
      radial-gradient(ellipse at 12% 0%, rgba(59, 139, 255, 0.08) 0%, transparent 50%),
      radial-gradient(ellipse at 88% 100%, rgba(0, 224, 240, 0.05) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.factory-dashboard {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  transition: opacity 0.4s, transform 0.4s;
}

.page.hidden {
  opacity: 0;
  pointer-events: none;
  transform: scale(0.97);
}

.page.visible {
  opacity: 1;
  pointer-events: auto;
  transform: scale(1);
}

.header-bar {
  height: 56px;
  flex-shrink: 0;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(
      180deg,
      rgba(16, 22, 38, 0.96) 0%,
      rgba(16, 22, 38, 0.75) 100%
  );
  border-bottom: 1px solid var(--border);
  backdrop-filter: blur(12px);
  position: relative;
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}

.logo-en {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  color: var(--accent);
  font-family: 'Rajdhani', sans-serif;
  display: block;
}

.logo-cn {
  font-size: 11px;
  color: var(--muted);
  letter-spacing: 1px;
}

.company-title {
  font-size: 18px;
  font-weight: 900;
  letter-spacing: 3px;
  font-family: 'Noto Sans SC', sans-serif;
  background: linear-gradient(90deg, var(--accent), var(--accent2));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.company-en {
  font-size: 11px;
  color: var(--muted);
  letter-spacing: 2px;
}

.header-date,
.header-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--muted);
}

.header-time {
  font-family: 'Rajdhani', 'Noto Sans SC', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: var(--accent);
  min-width: 80px;
  text-align: right;
}

.online-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #00e0f0;
}

.sdot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.sdot.running {
  background: var(--accent2);
  box-shadow: 0 0 8px rgba(0, 224, 240, 0.6);
  animation: pulse 1.5s infinite;
}

.sdot.waiting {
  background: var(--warn);
  box-shadow: 0 0 8px rgba(255, 179, 71, 0.6);
  animation: pulse 2s infinite;
}

.sdot.stopped {
  background: var(--danger);
  box-shadow: 0 0 8px rgba(255, 107, 122, 0.6);
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.3);
  }
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(77, 154, 255, 0.6), transparent);
  animation: scan 6s linear infinite;
  opacity: 0.6;
}

@keyframes scan {
  0% {
    top: 0;
    opacity: 0;
  }
  10% {
    opacity: 0.6;
  }
  90% {
    opacity: 0.6;
  }
  100% {
    top: 100%;
    opacity: 0;
  }
}

.main-container {
  flex: 1;
  display: flex;
  gap: 12px;
  padding: 12px;
  min-height: 0;
  overflow: hidden;
}

.aside-left,
.aside-right {
  width: 22%;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
}

.section-middle {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
  padding: 16px;
}

.card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--accent), var(--accent2), transparent);
  opacity: 0.5;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--accent);
  letter-spacing: 2px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--border);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.card-title::before {
  content: '';
  width: 3px;
  height: 14px;
  background: linear-gradient(180deg, var(--accent), var(--accent2));
  border-radius: 2px;
  flex-shrink: 0;
}

.quality-title {
  color: var(--quality);
}

.flex-col-grow {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.fade-in {
  animation: fadeIn 0.5s ease both;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.total-print-box {
  text-align: center;
  margin-bottom: 16px;
}

.sub-tip {
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 4px;
}

.big-num {
  font-family: 'Rajdhani', 'Noto Sans SC', sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: var(--accent);
  line-height: 1.1;
}

.two-col-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.stat-item {
  text-align: center;
  padding: 12px 8px;
  border-radius: 8px;
  background: var(--stat-bg);
  border: 1px solid var(--border);
}

.stat-item.cyan {
  background: rgba(0, 224, 240, 0.08);
}

.stat-item.blue {
  background: rgba(77, 154, 255, 0.08);
}

.stat-item.warn {
  background: rgba(255, 179, 71, 0.08);
}

.stat-val {
  font-family: 'Rajdhani', 'Noto Sans SC', sans-serif;
  font-size: 28px;
  font-weight: 700;
  margin-top: 4px;
}

.stat-val.cyan {
  color: var(--accent2);
}

.stat-val.blue {
  color: var(--accent);
}

.stat-val.warn {
  color: var(--warn);
}

.sub-pct {
  font-size: 13px;
  color: var(--muted);
  margin-top: 2px;
}

.ring-center-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;
  width: 120px;
  height: 120px;
  margin-left: auto;
  margin-right: auto;
}

.ring-inner-text {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ring-num {
  font-family: 'Rajdhani', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.ring-num.blue {
  color: var(--accent);
}

.ring-num.cyan {
  color: var(--accent2);
}

.ring-num.danger {
  color: var(--danger);
}

.ring-tip {
  font-size: 12px;
  color: var(--muted);
  margin-top: 4px;
}

.duration-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dur-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.2s;
}

.dur-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}

.dot.cyan {
  background: var(--accent2);
}

.dot.warn {
  background: var(--warn);
}

.dot.danger {
  background: var(--danger);
}

.dur-label {
  font-size: 14px;
  min-width: 56px;
  color: #f0f5fc;
}

.dur-bar {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--ring-bg);
  overflow: hidden;
}

.dur-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 1s ease;
}

.dur-fill.cyan {
  background: var(--accent2);
}

.dur-fill.warn {
  background: var(--warn);
}

.dur-fill.danger {
  background: var(--danger);
}

.dur-h {
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  font-weight: 600;
  min-width: 36px;
  text-align: right;
}

.dur-h.cyan {
  color: var(--accent2);
}

.dur-h.warn {
  color: var(--warn);
}

.dur-h.danger {
  color: var(--danger);
}

.dur-pct {
  font-size: 13px;
  color: var(--muted);
  min-width: 38px;
  text-align: right;
}

.env-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  flex: 1;
  min-height: 0;
}

.env-ring-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 8px;
  border-radius: 10px;
  background: var(--stat-bg);
  border: 1px solid var(--border);
}

.svg-ring {
  position: relative;
  width: 100px;
  height: 100px;

}

.svg-ring svg {
  width: 100%;
  height: 100%;
}

.env-label {
  font-size: 13px;
  color: var(--muted);
  margin-top: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
}

.equip-grid {
  display: grid;
  gap: 12px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}

.equip-card {
  cursor: pointer;
  transition: transform 0.22s, box-shadow 0.22s, border-color 0.22s;
  border: 1px solid var(--border);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  padding: 10px;
}

.equip-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 28px rgba(77, 154, 255, 0.18);
  border-color: rgba(77, 154, 255, 0.4);
}
.equip-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, var(--accent), var(--accent2), transparent);
  opacity: .5;
}

.equip-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.equip-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.equip-name {
  font-weight: 700;
  font-size: 15px;
  color: #f0f5fc;
}

.status-tag {
  font-size: 13px;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  white-space: nowrap;
}

.equip-thumb {
  width: 100%;
  aspect-ratio: 8/5;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid var(--border);
  flex-shrink: 0;
  position: relative;
  margin-bottom: 12px;
}

.equip-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.equip-card:hover .equip-thumb img {
  transform: scale(1.06);
}

.equip-thumb::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 30%, rgba(8, 12, 22, 0.5) 100%);
  pointer-events: none;
}

.equip-pie-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.pie-wrap {
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;
}

.pie-wrap canvas {
  width: 120px !important;
  height: 120px !important;
}

.pie-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.pie-val {
  font-family: 'Rajdhani', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: var(--accent);
  line-height: 1;
}

.pie-lbl {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

.pie-data-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
}

.data-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 6px;
}
.data-row .title {
  color: #f0f5fc;
}
.data-row.cyan-bg {
  background: rgba(0, 224, 240, 0.06);
}

.data-row.warn-bg {
  background: rgba(255, 179, 71, 0.06);
}

.data-row-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mini-dot {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.mini-dot.cyan {
  background: var(--accent2);
}

.mini-dot.blue {
  background: var(--accent);
}

.mini-dot.warn {
  background: var(--warn);
}

.data-num {
  font-family: 'Rajdhani', sans-serif;
  font-weight: 600;
}

.data-num.cyan {
  color: var(--accent2);
}

.data-num.warn {
  color: var(--warn);
}

.order-stat-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.stat-mini {
  text-align: center;
  padding: 6px 4px;
  border-radius: 6px;
  background: var(--stat-bg);
}

.stat-mini-tip {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 2px;
}

.stat-mini-val {
  font-family: 'Rajdhani', sans-serif;
  font-size: 15px;
  font-weight: 700;
}

.stat-mini-val.cyan {
  color: var(--accent2);
}

.stat-mini-val.warn {
  color: var(--warn);
}

.order-overview-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.overview-pie-wrap {
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;
}

.overview-pie-wrap canvas {
  width: 120px !important;
  height: 120px !important;
}

.overview-stat-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14px;
}

.ov-stat {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 8px;
}

.ov-stat.cyan-bg {
  background: rgba(0, 224, 240, 0.07);
}

.ov-stat.blue-bg {
  background: rgba(77, 154, 255, 0.07);
}

.ov-stat.warn-bg {
  background: rgba(255, 179, 71, 0.07);
}

.ov-left {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f0f5fc;
}

.ov-num {
  font-family: 'Rajdhani', sans-serif;
  font-size: 16px;
  font-weight: 700;
}

.ov-num.cyan {
  color: var(--accent2);
}

.ov-num.blue {
  color: var(--accent);
}

.ov-num.warn {
  color: var(--warn);
}

.order-scroll-card {
  min-height: 0;
}

.scroll-container {
  overflow: hidden;
  position: relative;
  flex: 1;
  min-height: 0;
}

.scroll-inner {
  animation: none;
}

.order-scroll-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  gap: 8px;
}

.order-scroll-item:hover {
  background: rgba(77, 154, 255, 0.04);
}

.order-info {
  flex: 1;
  min-width: 0;
}

.order-name {
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #f0f5fc;
}

.order-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

.order-qty-box {
  text-align: right;
  flex-shrink: 0;
  min-width: 60px;
}

.qty-num {
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--accent);
}

.qty-tip {
  font-size: 12px;
  color: var(--muted);
}

.tag {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 5px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.tag-completed {
  background: rgba(0, 224, 240, 0.14);
  color: var(--accent2);
}

.tag-inProgress {
  background: rgba(77, 154, 255, 0.14);
  color: var(--accent);
}

.tag-pending {
  background: rgba(255, 179, 71, 0.14);
  color: var(--warn);
}

.scroll-tip {
  margin-left: auto;
  font-size: 13px;
  font-weight: normal;
  color: var(--muted);
  letter-spacing: 0;
}

/* 详情页 */
.page-detail {
  background: var(--bg);
}

.back-btn {
  background: rgba(77, 154, 255, 0.12);
  border: 1px solid rgba(77, 154, 255, 0.3);
  color: var(--accent);
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.back-btn:hover {
  background: rgba(77, 154, 255, 0.22);
  border-color: var(--accent);
}

.split-line {
  width: 1px;
  height: 20px;
  background: var(--border);
}

.detail-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--accent);
}

.detail-main {
  overflow: hidden;
}

.detail-col-left,
.detail-col-right {
  width: 22%;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
  min-height: 0;
  overflow-y: auto;
}

.detail-col-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  min-height: 0;
}

.center-pie-wrap {
  display: flex;
  justify-content: center;
  margin-top: 16px;
  position: relative;
  width: 120px;
  height: 120px;
  margin-left: auto;
  margin-right: auto;
}

.center-pie-wrap canvas {
  width: 120px !important;
  height: 120px !important;
}

.detail-order-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.equip-detail-top {
  display: flex;
  gap: 16px;
}

.detail-thumb {
  width: 200px;
  height: 125px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border);
  flex-shrink: 0;
}

.detail-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-info-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  font-size: 14px;
}

.info-cell {
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--stat-bg);
}

.info-label {
  font-size: 13px;
  color: var(--muted);
}

.info-text {
  margin-top: 4px;
  font-weight: 700;
  font-size: 16px;
  color: #f0f5fc;
}

.job-data-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.job-stat-item {
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--stat-bg);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.job-label {
  font-size: 13px;
  color: var(--muted);
  display: flex;
  align-items: center;
  gap: 6px;
}

.job-label i {
  color: var(--accent);
  font-size: 14px;
}

.job-val {
  font-family: 'Rajdhani', 'Noto Sans SC', sans-serif;
  font-weight: 700;
  font-size: 22px;
  line-height: 1.15;
  color: #f0f5fc;
}

.job-val.cyan {
  color: var(--accent2);
}

.job-val.blue {
  color: var(--accent);
}

.job-val.warn {
  color: var(--warn);
}

.job-val.danger {
  color: var(--danger);
}

.job-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

.two-col-chart-row {
  display: flex;
  gap: 12px;
  flex: 1;
  min-height: 0;
}

.chart-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.chart-card canvas {
  flex: 1;
  min-height: 0;
}

.print-data-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.print-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background 0.2s;
}

.print-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.print-label {
  font-size: 14px;
  color: #f0f5fc;
}

.print-num {
  font-family: 'Rajdhani', sans-serif;
  font-size: 14px;
  font-weight: 600;
  min-width: 44px;
  text-align: right;
}

.print-num.blue {
  color: var(--accent);
}

.print-num.cyan {
  color: var(--accent2);
}

.print-num.warn {
  color: var(--warn);
}

.print-pct {
  font-family: 'Rajdhani', sans-serif;
  font-size: 13px;
  font-weight: 600;
  min-width: 44px;
  text-align: right;
}

.print-pct.cyan {
  color: var(--accent2);
}

.print-pct.warn {
  color: var(--warn);
}

.water-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.water-grid .fa-temperature-high.blue {
  color: var(--accent);
}
.water-grid .fa-flask.cyan {
  color: var(--accent2);
}
.water-grid .fa-bolt.warn {
  color: var(--warn);
}
.water-item {
  text-align: center;
  padding: 8px 6px;
  border-radius: 8px;
  background: var(--stat-bg);
  border: 1px solid var(--border);
}

.water-item i {
  font-size: 14px;
}

.water-val {
  font-family: 'Rajdhani', sans-serif;
  font-size: 22px;
  font-weight: 700;
  margin: 4px 0 2px;
}

.water-val.blue {
  color: var(--accent);
}

.water-val.cyan {
  color: var(--accent2);
}

.water-val.warn {
  color: var(--warn);
}

.water-tip {
  font-size: 12px;
  color: var(--muted);
}

.oee-item {
  margin-bottom: 12px;
}

.oee-item:last-child {
  margin-bottom: 0;
}

.oee-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.oee-label {
  font-size: 14px;
  color: var(--muted);
}

.oee-num {
  font-family: 'Rajdhani', sans-serif;
  font-size: 18px;
  font-weight: 700;
}

.oee-num.blue {
  color: var(--accent);
}

.oee-bar {
  height: 8px;
  border-radius: 4px;
  background: var(--ring-bg);
  overflow: hidden;
}

.oee-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 1s ease;
}

.oee-fill.blue {
  background: linear-gradient(90deg, var(--accent2), var(--accent));
}

.oee-fill.purple {
  background: linear-gradient(90deg, var(--accent), #8b6aff);
}

.maintenance-card {
  flex-shrink: 0;
}

.maintenance-empty {
  text-align: center;
  padding: 12px;
  color: var(--muted);
  font-size: 14px;
}

.maintenance-empty i {
  color: var(--accent2);
  margin-right: 6px;
}

.maintenance-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 179, 71, 0.07);
  border: 1px solid rgba(255, 179, 71, 0.14);
  transition: background 0.2s;
}

.maintenance-row:hover {
  background: rgba(255, 179, 71, 0.12);
}

.mp {
  color: var(--warn);
}

.main-text {
  flex: 1;
}

.main-name {
  font-size: 14px;
  font-weight: 500;
  color: #f0f5fc;
}

.main-desc {
  font-size: 13px;
  margin-top: 2px;
}

.main-tag {
  font-size: 14px;
  font-weight: 600;
}

/* 滚动条 */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(77, 154, 255, 0.18);
  border-radius: 2px;
}

/* 响应适配 */
@media screen and (max-width: 1200px) {
  .aside-left,
  .aside-right {
    width: 24%;
  }

  .equip-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .job-data-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .two-col-chart-row {
    flex-direction: column;
  }
}

@media screen and (max-width: 768px) {
  .header-bar {
    padding: 0 12px;
  }

  .header-center {
    display: none;
  }

  .main-container {
    flex-direction: column;
    overflow-y: auto;
  }

  .aside-left,
  .aside-right,
  .section-middle {
    width: 100%;
    min-width: 0;
  }

  .equip-grid {
    grid-template-columns: 1fr !important;
  }

  .job-data-grid {
    grid-template-columns: 1fr;
  }

  .equip-detail-top {
    flex-direction: column;
  }

  .detail-thumb {
    width: 100%;
    height: auto;
    aspect-ratio: 16/10;
  }

  .detail-info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
<style lang="scss" scoped>
.svg-ring {
  position: relative;
  width: 100px;
  height: 100px;

  // 环形进度外层容器
  ::v-deep .el-progress {
    width: 100%;
    height: 100%;
  }
  // 隐藏组件自带文字
  ::v-deep .el-progress__text {
    display: none !important;
  }
  // 插槽自定义文字绝对居中
  ::v-deep .el-progress-circle {
    position: relative;
  }
  ::v-deep .progress-inner {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  .ring-num {
    font-family: 'Rajdhani', sans-serif;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.1;
  }
  .ring-num.danger {
    color: #ff6b7a;
  }
  .ring-num.blue {
    color: #4d9aff;
  }
  .ring-tip {
    font-size: 12px;
    color: #94a7c8;
    margin-top: 4px;
  }
}
.svg-ring .progress-inner {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
</style>
